<template>
  <div class="fts-form-designer fts-element-designer">
    <!-- element design content -->
    <div class="form-designer-content element-designer-content fts-form-group">
      <!-- element design tool -->
      <div class="element-designer-tool fts-scrollbar">
        <!-- information -->
        <div class="section-title">
          Information
          <fts-tippy content="Add new information">
            <fts-btn
              slot="trigger"
              sm
              outline
              :color="information && information.length ? 'primary' : 'secondary'"
              text-transform-init
              @click="openInformationDesignModal"
            >
              <font-awesome-icon :icon="['fas', 'plus']" />Add
            </fts-btn>
          </fts-tippy>
        </div>
        <template v-if="information && information.length">
          <div class="fts-form-control-filter information-filter">
            <input
              class="fts-form-control"
              v-model="informationFilter"
              placeholder="Search something for..."
            />
            <div class="search-icon">
              <font-awesome-icon :icon="['fas', 'search']" fixed-width />
            </div>
          </div>
          <!-- empty -->
          <div
            class="information-no-found"
            v-if="nonClassification && !nonClassification.length"
          >
            <div class="information-no-found-icon">
              <font-awesome-icon :icon="['fas', 'search']" />
            </div>
            <div class="information-no-found-label">
              No results found.
            </div>
            <fts-btn
              text
              color="primary"
              text-transform-init
              class="information-no-found-action"
              @click="openInformationDesignModal({ displayName: informationFilter })"
            >
              <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
              Add New
            </fts-btn>
          </div>
          <!-- information content -->
          <draggable
            v-model="nonClassification"
            :group="{ name: 'row', pull: 'clone', put: false }"
            :clone="clone"
            v-bind="dragItemOptions"
            v-if="nonClassification.length"
          >
            <div
              v-for="field in nonClassification"
              :key="field.id"
              :class="{ 'draggable-tool-item': !disableClassification(field) }"
            >
              <fts-design-tool-item
                :item="field"
                :disabled-remove="true"
                :disabled="disableClassification(field)"
              />
            </div>
          </draggable>
        </template>
        <!-- no information -->
        <div class="information-no-found" v-else>
          <div class="information-no-found-icon">
            <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
          </div>
          <div class="information-no-found-label">
            No information added.
          </div>
          <div class="information-no-found-helper">
            Before start to customize your form, you will need to add information.
          </div>
          <fts-btn
            text
            color="primary"
            text-transform-init
            class="information-no-found-action"
            @click="openInformationDesignModal()"
          >
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
            Add New
          </fts-btn>
        </div>
        <!-- elements -->
        <div class="section-title">Elements</div>
        <draggable
          v-model="getPrefix"
          :group="{ name: 'row', pull: 'clone', put: false }"
          :clone="clone"
          draggable=".draggable-tool-item"
          v-bind="dragOptions"
        >
          <div
            class="draggable-tool-item"
            v-for="pre in getPrefix"
            :key="pre.key"
          >
            <fts-design-tool-item :item="pre"></fts-design-tool-item>
          </div>
          <div
            class="prefix-action"
            slot="footer"
            @click="prefixAdvanced = !prefixAdvanced"
          >
            {{ prefixAdvanced ? "less..." : "more..." }}
          </div>
        </draggable>
      </div>
      <!-- element designer container -->
      <div class="element-designer-container">
        <!-- control -->
        <div class="element-designer-control" v-if="previewDesignAction">
          <fts-btn
            sm
            outline
            color="primary"
            text-transform-init
            @click="previewDesign"
          >
            {{ preview ? "Design" : "Preview" }}
          </fts-btn>
        </div>
        <!-- element designer preview -->
        <div class="element-design-preview" v-if="preview && !document">
          <div class="element-design-preview__header">
            <div class="element-design-preview-header-l">
              <div class="preview-header-title" />
              <div class="preview-header-info">
                <div class="preview-header-document-id" />
                <div class="preview-header-audit">
                  <div class="preview-header-audit-profile" />
                  <div class="preview-header-audit-name" />
                </div>
              </div>
            </div>
            <div class="element-design-preview-header-r">
              <div class="preview-header-state" />
              <div class="preview-header-action" />
              <div class="preview-header-action" />
              <div class="preview-header-action" />
            </div>
          </div>
          <div class="element-design-preview-form fts-scrollbar">   
            <fts-form
              :vector-id="moduleId"
              :formData="formData"
              :form="form"
            />
            <div class="element-design-preview__footer">
              <div class="preview-header-action" />
              <div class="preview-header-action" />
            </div>
          </div>
        </div>
        <!-- form layout -->
        <fts-form-layouter
          :form="form"
          @added="elementAdded"
          :informationSource="information"
          :document="document"
          :itemGroup="'row'"
          :activityId="activityId"
          :activityDesign="form"
          v-else
        />
      </div>
    </div>
    <!-- automation -->
    <div v-if="beta && !document">
      <div>
        <fts-btn
          text
          text-transform-init
          @click="form.automation = null"
          v-if="Boolean(form.automation)"
        >
          Remove Automation
        </fts-btn>
        <fts-btn
          text
          color="primary"
          text-transform-init
          @click="setupAutomation()"
          v-else
        >
          Add Automation
        </fts-btn>
      </div>
      <!-- automation -->
      <fts-automation-designer
        :moduleId="moduleId"
        :data="form.automation"
        :isForm="true"
        class="automation-design-content"
        v-if="form.automation"
      />
    </div>
    <!-- information design modal -->
    <fts-information-design-modal ref="ftsInformationDesignModal" />
  </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";
import VueJsonPretty from "vue-json-pretty";
import wysiwyg from "../vue-wysiwyg/src/editor/index";
Vue.use(wysiwyg, {});
// import pdf from "vue-pdf";
import pdf from "vue-pdf/src/vuePdfNoSss";

import commons from "../commons/commons";
import { store as canvasDesignStore } from "../store/canvasDesignStore";

import FTSDesignElement from "./FTSDesignElement";
import FTSDesignToolItem from "./FTSDesignToolItem";
import FTSForm from "./FTSForm";
import FTSFormLayouter from "../design/FTSFormLayouter";
import FTSBtn from "./FTSBtn";
import FTSAutomationDesigner from "../design/automation/FTSAutomationDesigner.vue";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";
import FTSInformationDesignModal from "../design/FTSInformationDesignModal";
import FTSTippy from "../elements/FTSTippy";

export default {
  components: {
    draggable,
    VueJsonPretty,
    pdf,
    "fts-design-element": FTSDesignElement,
    "fts-design-tool-item": FTSDesignToolItem,
    "fts-form": FTSForm,
    "fts-btn": FTSBtn,
    "fts-automation-designer": FTSAutomationDesigner,
    "fts-release-badge": FTSReleaseBadge,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-tippy": FTSTippy,
  },
  props: {
    classification: Array,
    document: Boolean,
    documentData: Object,
    activityId: String,
    previewDesignAction: Boolean,
    form: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      previewDocument: null,
      preview: false,
      formData: {},
      documentPrefix: [
        {
          input: "description",
          content: "enter your text",
        },
        {
          input: "image",
          url: null,
          item: {
            formType: "design",
            input: "imageUpload",
            fieldName: "imageSource",
          },
        },
        { input: "empty" },
        { input: "section", type: "section", design: { rows: [] } },
        {
          input: "section",
          type: "column",
          design: { rows: [] },
          advanced: true,
        },
      ],
      prefix: [
        {
          input: "description",
          content: "enter your text",
        },
        {
          input: "image",
          url: null,
          item: {
            formType: "design",
            input: "imageUpload",
            fieldName: "imageSource",
          },
        },
        { input: "empty" },
        {
          input: "custom",
          custom: true,
          beta: true,
          advanced: true,
        },
        {
          input: "button",
          beta: true,
          advanced: true,
        },
        { input: "section", type: "section", design: { rows: [] } },
        {
          input: "section",
          type: "collapse",
          design: { rows: [] },
          advanced: true,
        },
        {
          input: "section",
          type: "tab",
          design: { rows: [] },
          tabs: [
            {
              id: commons.nanoID(),
              header: "(new tab)",
              design: { rows: [] },
            },
          ],
          advanced: true,
        },
        {
          input: "section",
          type: "column",
          design: { rows: [] },
          advanced: true,
        },
      ],
      prefixAdvanced: false,
      enabled: true,
      myHtml: null,
      dragOptions: {
        animation: 150,
        ghostClass: "ghost",
        forceFallback: true,
      },
      dragItemOptions: {
        animation: 150,
        ghostClass: "ghost",
        draggable: ".draggable-tool-item",
        forceFallback: true,
      },
      informationFilter: null,
    };
  },
  computed: {
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    beta() {
      return this.$store.state.beta;
    },
    getPrefix() {
      if (this.document) {
        return this.documentPrefix;
      }

      return this.prefix.filter(
        (x) =>
          ((this.prefixAdvanced && x.advanced) || !x.advanced) &&
          ((this.beta && x.beta) || !x.beta)
      );
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    selection() {
      if (this.classification == null) return [];
      let classification = this.classification;

      if (this.informationFilter) {
        return classification.filter((x) => {
          return (
            x.displayName &&
            x.displayName
              .toLowerCase()
              .indexOf(this.informationFilter.toLowerCase()) != -1
          );
        });
      } else {
        return classification;
      }
    },
    nonClassification() {
      let information = [];

      if (this.information == null) return [];

      information = this.information;

      if (this.informationFilter) {
        return information.filter((x) => {
          return (
            x.displayName
              .toLowerCase()
              .indexOf(this.informationFilter.toLowerCase()) != -1
          );
        });
      } else {
        return information;
      }
    },
  },
  methods: {
    setupAutomation() {
      if (!this.form.automation) {
        Vue.set(this.form, "automation", {
          testLocusId: null,
          testLocus: null,
          testLocusDetailId: null,
          testLocusDetail: null,
        });
        this.form.automation.blocks = {
          1: {
            id: 1,
            name: "start",
            data: {
              id: "start",
              variables: [],
            },
            class: "start",
            html: "start",
            typenode: "vue",
            outputs: {
              output_1: {
                connections: [],
              },
            },
            inputs: {},
            pos_x: 200,
            pos_y: 50,
          },
        };
      }

      this.form.automation.blocks[1].data.variables = [
        {
          id: "form",
          fieldName: "form",
          name: "Form",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
          activityId: this.activityId
        },
        {
          id: "entry",
          fieldName: "entry",
          name: "Entry",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
        },
        {
          id: "initial",
          fieldName: "initial",
          name: "Initial",
          nodeId: 1,
          type: 1,
        },
        {
          id: "query",
          fieldName: "query",
          name: "Query",
          nodeId: 1,
          type: 37,
        },
        {
          id: "interaction",
          fieldName: "interaction",
          name: "Interaction",
          nodeId: 1,
          type: 37,
        },
      ];
    },
    previewDesign() {
      if (!this.document) {
        this.preview = !this.preview;
        this.$emit("previewChanged", this.preview);
      }
    },
    disableClassification(item) {
      if (this.form.rows) {
        let items = commons.classificationInformation(this.form.rows, this.information);
        if (items && item.id) {
          return (
            items
              .filter(x => x)
              .map(x => x.id)
              .indexOf(item.id) > -1
          );
        }
      }

      return false;
    },
    elementAdded(element) {
      if (element.custom) {
        if (!element.id) element.id = commons.nanoID();
      } else if (
        !element.id &&
        this.prefix.some((x) => x.input === element.input)
      ) {
        this.$set(element, "id", commons.nanoID());
      }
    },
    modalConfirmation(arg) {
      let buttons = [{ title: "Cancel" }];
      let text = "Confirmation Dialog";

      if (arg === "reset") {
        buttons.push({
          title: "Auto Arrange",
          default: true,
          handler: () => {
            this.reset();
            this.$modal.hide("dialog");
          },
        });
        text = "Auto Arrange Canvas Form Design ?";
      } else if (arg === "clearAll") {
        buttons.push({
          title: "Clear All",
          default: true,
          handler: () => {
            this.clearAll();
            this.$modal.hide("dialog");
          },
        });
        text = "Clear All Canvas Form Design ?";
      }

      this.$modal.show("dialog", {
        title: "Confirmation",
        text: text,
        buttons: buttons,
      });
    },
    clone(item) {
      return lodash.cloneDeep(item);
    },
    clearAll() {
      this.form.rows = [
        {
          index: 0,
          items: [],
        },
      ];
    },
    reset() {
      this.form.rows = [{ index: 0, items: [] }];
      this.autoForm();
    },
    autoForm() {
      if (this.selection.length > 0) {
        this.selection.forEach((element, key, arr) => {
          // Retrieve Element Size
          let getSize = commons.informationTypes.find((item) => {
            return item.id === element.type;
          });
          if (getSize) element.size = getSize.size;
          // New Form Rows
          if (this.form.rows.length === 0) {
            this.form.rows.push({ index: 0, items: [] });
          }
          // Logic Auto Form
          let lastFormRow = this.form.rows[this.form.rows.length - 1].items;
          var allowIn = true;

          if (
            lastFormRow.length > 0 &&
            _.find(lastFormRow, (row) => {
              return row.size <= lastFormRow.length;
            })
          )
            allowIn = false;

          if (element.size > lastFormRow.length && allowIn) {
            this.form.rows[this.form.rows.length - 1].items.push(element);
          } else {
            if (lastFormRow.length === 1 && _.last(lastFormRow).size !== 1) {
              this.form.rows[this.form.rows.length - 1].items.push({
                input: "empty",
              });
            }
            this.form.rows.push({ index: this.form.rows.length, items: [] });
            this.form.rows[this.form.rows.length - 1].items.push(element);
          }

          if (
            this.form.rows[this.form.rows.length - 1].items.length === 1 &&
            this.form.rows[this.form.rows.length - 1].items[0].size !== 1 &&
            Object.is(arr.length - 1, key)
          ) {
            this.form.rows[this.form.rows.length - 1].items.push({
              input: "empty",
            });
          }
          //End
        });
      }
    },
    async openInformationDesignModal(selected) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      let item = {
        id: commons.generateFieldName(),
        fieldName: commons.generateFieldName(),
        ...selected,
      }

      function set(result) {
        if (!canvas.information || !commons.isArray(canvas.information)) {
          canvas.information = [];
        } else {
          canvas.information.push(result);
        }
        /* update vector canvas */
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      }

      const result = await this.$refs.ftsInformationDesignModal.show(
        item,
        null,
        continueFormData => {
          set(continueFormData),
          this.openInformationDesignModal();
        }
      );

      if (result != null && result != true) {
        set(result);

        if (this.informationFilter)
          this.informationFilter = result.displayName;
      }
    },
  },
  beforeCreate: function () {
    Vue.component("fts-form-layouter", FTSFormLayouter);
    Vue.component("fts-design-element", FTSDesignElement);
    Vue.component("fts-form", FTSForm);
  },
  mounted() {
    if (this.form.automation) {
      this.form.automation.blocks[1].data.variables = [
        {
          id: "form",
          fieldName: "form",
          name: "Form",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
          activityId: this.activityId
        },
        {
          id: "entry",
          fieldName: "entry",
          name: "Entry",
          nodeId: 1,
          type: 7,
          moduleId: this.moduleId,
        },
        {
          id: "initial",
          fieldName: "initial",
          name: "Initial",
          nodeId: 1,
          type: 1,
        },
        {
          id: "query",
          fieldName: "query",
          name: "Query",
          nodeId: 1,
          type: 37,
        },
        {
          id: "interaction",
          fieldName: "interaction",
          name: "Interaction",
          nodeId: 1,
          type: 37,
        },
      ];
    }
  },
};
</script>
<style lang="scss">
@import "~vue-wysiwyg/dist/vueWysiwyg.css";
@import "./src/assets/scss/vars.scss";

.fts-form-designer {
  flex: 1;
  display: flex;
  flex-direction: column;
  .form-designer-header {
    display: flex;
    justify-content: space-between;
    margin: 1rem 0.75rem;
  }
  .form-designer-content {
    /* custom class */
    .prefix-action {
      display: inline-flex;
      margin: 0.375rem 0.375rem 0.75rem;
      font-size: 0.75rem;
      color: var(--fts-primary);
      &:hover {
        cursor: pointer;
      }
    }
    /* information filter */
    .information-filter {
      margin-top: .375rem;
      margin-bottom: 1rem;
      > input {
        border-radius: .25rem;
      }
    }
    /* information no found */
    .information-no-found {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 1.5rem .375rem;
      padding-left: .375rem;
      padding-right: .375rem;
      text-align: center;
      .information-no-found-icon {
        @include flex-center-middle;
        height: 2rem;
        width: 2rem;
        border-radius: .575rem;
        background-color: var(--fts-gray-2);
        color: var(--fts-gray-5);
      }
      .information-no-found-label {
        margin-top: .75rem;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1.5rem;
        color: var(--fts-form-label);
      }
      .information-no-found-helper {
        margin-top: .175rem;
        font-size: .75rem;
        line-height: 1rem;
        color: var(--fts-form-helper);
      }
      .information-no-found-action {
        margin-top: .575rem;
      }
    }
  }
  .automation-design-content {
    margin-top: 1rem;
  }
}
</style>
